import React, { useState, useEffect } from 'react';
import { salesAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from './ui/dialog';
import { Handshake, Plus, PencilSimple, Trash, MagnifyingGlass, Phone, EnvelopeSimple, CalendarBlank, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const STATUS_OPTIONS = [
  { value: 'new_lead', label: 'New Lead', cls: 'bg-blue-100 text-blue-800' },
  { value: 'follow_up', label: 'Follow-Up', cls: 'bg-amber-100 text-amber-800' },
  { value: 'converted', label: 'Converted', cls: 'bg-emerald-100 text-emerald-800' },
  { value: 'lost', label: 'Lost', cls: 'bg-gray-200 text-gray-700' },
];
const statusMeta = (v) => STATUS_OPTIONS.find(s => s.value === v) || STATUS_OPTIONS[0];

const EMPTY_FORM = {
  visit_date: format(new Date(), 'yyyy-MM-dd'),
  contact_name: '', company: '', email: '', phone: '',
  status: 'new_lead', follow_up_date: '', notes: ''
};

export const SalesCallsTab = () => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try { const res = await salesAPI.getAll(); setCalls(res.data); }
    catch (e) { toast.error('Failed to load sales calls'); }
    finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      visit_date: c.visit_date || '', contact_name: c.contact_name || '',
      company: c.company || '', email: c.email || '', phone: c.phone || '',
      status: c.status || 'new_lead', follow_up_date: c.follow_up_date || '', notes: c.notes || ''
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.contact_name.trim()) { toast.error('Contact name is required'); return; }
    if (!form.visit_date) { toast.error('Visit date is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, follow_up_date: form.follow_up_date || null };
      if (editing) {
        await salesAPI.update(editing.id, payload);
        toast.success('Sales call updated');
      } else {
        await salesAPI.create(payload);
        toast.success('Sales call logged');
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try {
      await salesAPI.delete(deleteTarget.id);
      toast.success('Sales call deleted');
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const today = format(new Date(), 'yyyy-MM-dd');
  const filtered = calls.filter(c => {
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return [c.contact_name, c.company, c.email, c.phone, c.notes].some(v => (v || '').toLowerCase().includes(t));
  });
  const overdueCount = calls.filter(c => c.follow_up_date && c.follow_up_date <= today && !['converted', 'lost'].includes(c.status)).length;

  return (
    <div className="space-y-6" data-testid="sales-calls-tab">
      <div className="flex justify-end">
        <Button className="rounded-xl gap-2" onClick={openCreate} data-testid="add-sales-call-btn">
          <Plus weight="bold" className="w-4 h-4" /> Log Sales Call
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryBox label="Total Calls" value={calls.length} testid="sales-total-calls" />
        <SummaryBox label="New Leads" value={calls.filter(c => c.status === 'new_lead').length} testid="sales-new-leads" />
        <SummaryBox label="Converted" value={calls.filter(c => c.status === 'converted').length} testid="sales-converted" />
        <SummaryBox label="Overdue Follow-Ups" value={overdueCount} highlight={overdueCount > 0} testid="sales-overdue" />
      </div>

      {/* List */}
      <Card className="rounded-2xl border border-border" data-testid="sales-calls-card">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <CardTitle className="font-heading text-lg">Sales Calls</CardTitle>
              <CardDescription>{filtered.length} of {calls.length} entries</CardDescription>
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36 rounded-xl text-sm" data-testid="sales-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="relative w-56">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 rounded-xl text-sm" data-testid="sales-search" />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-sm text-muted-foreground">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <Handshake weight="duotone" className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No sales calls yet. Click "Log Sales Call" to add your first.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="sales-calls-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Visit Date</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Follow-Up</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((c, i) => {
                    const overdue = c.follow_up_date && c.follow_up_date <= today && !['converted', 'lost'].includes(c.status);
                    return (
                      <TableRow key={c.id} className={i % 2 === 0 ? '' : 'bg-muted/30'} data-testid={`sales-call-row-${i}`}>
                        <TableCell className="text-sm whitespace-nowrap">{c.visit_date}</TableCell>
                        <TableCell className="text-sm font-medium">{c.contact_name}</TableCell>
                        <TableCell className="text-sm">{c.company || '—'}</TableCell>
                        <TableCell className="text-sm">
                          {c.email ? <a href={`mailto:${c.email}`} className="text-blue-700 hover:underline flex items-center gap-1"><EnvelopeSimple className="w-3.5 h-3.5" />{c.email}</a> : '—'}
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap">
                          {c.phone ? <a href={`tel:${c.phone}`} className="text-blue-700 hover:underline flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{c.phone}</a> : '—'}
                        </TableCell>
                        <TableCell>
                          <Badge className={`rounded-full text-xs ${statusMeta(c.status).cls}`}>{statusMeta(c.status).label}</Badge>
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap">
                          {c.follow_up_date ? (
                            <span className={`flex items-center gap-1 ${overdue ? 'text-red-600 font-medium' : ''}`}>
                              {overdue ? <WarningCircle weight="bold" className="w-3.5 h-3.5" /> : <CalendarBlank className="w-3.5 h-3.5" />}
                              {c.follow_up_date}
                            </span>
                          ) : '—'}
                        </TableCell>
                        <TableCell className="text-sm max-w-[240px]">
                          <p className="truncate" title={c.notes || ''}>{c.notes || '—'}</p>
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button variant="ghost" size="sm" className="rounded-lg h-8 w-8 p-0" onClick={() => openEdit(c)} data-testid={`edit-sales-call-${i}`}>
                            <PencilSimple className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="rounded-lg h-8 w-8 p-0 text-red-600 hover:text-red-700" onClick={() => setDeleteTarget(c)} data-testid={`delete-sales-call-${i}`}>
                            <Trash className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg rounded-2xl" data-testid="sales-call-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Edit Sales Call' : 'Log Sales Call'}</DialogTitle>
            <DialogDescription>Record the details of the sales visit</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Visit Date *</Label>
                <Input type="date" value={form.visit_date} onChange={e => setForm({ ...form, visit_date: e.target.value })} className="rounded-xl" data-testid="sales-form-visit-date" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="sales-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Contact Name *</Label>
                <Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} placeholder="Jane Smith" className="rounded-xl" data-testid="sales-form-contact-name" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Company / Organization</Label>
                <Input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder="Sunrise Senior Center" className="rounded-xl" data-testid="sales-form-company" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="jane@example.com" className="rounded-xl" data-testid="sales-form-email" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Phone</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" className="rounded-xl" data-testid="sales-form-phone" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Follow-Up Date (reminder)</Label>
              <Input type="date" value={form.follow_up_date} onChange={e => setForm({ ...form, follow_up_date: e.target.value })} className="rounded-xl" data-testid="sales-form-follow-up-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Visit Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="How the visit went, next steps, who to contact..." rows={4} className="rounded-xl" data-testid="sales-form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setDialogOpen(false)} data-testid="sales-form-cancel">Cancel</Button>
            <Button className="rounded-xl" onClick={save} disabled={saving} data-testid="sales-form-save">
              {saving ? 'Saving...' : editing ? 'Save Changes' : 'Log Call'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="sales-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Delete Sales Call?</DialogTitle>
            <DialogDescription>
              This will permanently remove the sales call for <span className="font-medium">{deleteTarget?.contact_name}</span>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setDeleteTarget(null)} data-testid="sales-delete-cancel">Cancel</Button>
            <Button variant="destructive" className="rounded-xl" onClick={confirmDelete} data-testid="sales-delete-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SummaryBox = ({ label, value, highlight, testid }) => (
  <Card className={`rounded-2xl border ${highlight ? 'border-red-200 bg-red-50/50' : 'border-border'}`} data-testid={testid}>
    <CardContent className="p-4">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`font-heading text-2xl font-semibold mt-1 ${highlight ? 'text-red-600' : ''}`}>{value}</p>
    </CardContent>
  </Card>
);
